import { useCallback, useState } from "react";

// ---------------------------------------------------------------------------
// useChannelInspectorState — per-channel persistence shared across the base
// sheet and the overlay sheet.
//
// Compose draft, active tab, and selected artifact are keyed by channelId so
// they survive a channel moving between the base slot and the overlay slot
// (e.g. open channel from fleet header → click a member promotes the channel
// to overlay). The setters no-op on unchanged values to keep report-up
// effects from looping.
// ---------------------------------------------------------------------------

export interface ChannelInspectorState {
  draftFor: (channelId: string) => string;
  setDraft: (channelId: string, value: string) => void;
  tabFor: (channelId: string) => string;
  setTab: (channelId: string, tab: string) => void;
  artifactFor: (channelId: string) => string | null;
  setArtifact: (channelId: string, artifactId: string | null) => void;
}

export function useChannelInspectorState(): ChannelInspectorState {
  const [draftsByChannel, setDraftsByChannel] = useState<Map<string, string>>(
    () => new Map(),
  );
  const [tabByChannel, setTabByChannel] = useState<Map<string, string>>(
    () => new Map(),
  );
  const [artifactByChannel, setArtifactByChannel] = useState<
    Map<string, string | null>
  >(() => new Map());

  const setDraft = useCallback((id: string, value: string) => {
    setDraftsByChannel((prev) =>
      prev.get(id) === value ? prev : new Map(prev).set(id, value),
    );
  }, []);
  const setTab = useCallback((id: string, tab: string) => {
    setTabByChannel((prev) =>
      prev.get(id) === tab ? prev : new Map(prev).set(id, tab),
    );
  }, []);
  const setArtifact = useCallback((id: string, artifactId: string | null) => {
    setArtifactByChannel((prev) =>
      prev.get(id) === artifactId ? prev : new Map(prev).set(id, artifactId),
    );
  }, []);

  const draftFor = useCallback(
    (id: string) => draftsByChannel.get(id) ?? "",
    [draftsByChannel],
  );
  const tabFor = useCallback(
    (id: string) => tabByChannel.get(id) ?? "chat",
    [tabByChannel],
  );
  const artifactFor = useCallback(
    (id: string) => artifactByChannel.get(id) ?? null,
    [artifactByChannel],
  );

  return { artifactFor, draftFor, setArtifact, setDraft, setTab, tabFor };
}
